import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '@cpg-its/ads.common.loading-circle';
import { Grid, Hidden } from '@material-ui/core';
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { sortTableStyles } from '../../../../global/constants/theme/styles/tables/generic-sort-table';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ListPagination } from '../../../../search/components/list-pagination';
import { SearchApp } from '../../../../search/components/search-app';
import { SearchList } from '../../../../search/components/search-list';
import { SearchProps, GuidedProcessSupportProps } from '../../models/arc-epi-props';
import { unloadCompensationState } from '../compensation/compensation-history/compensation-history-slice';
import { unloadPersonState } from '../../../account-info/features/personal-info/personal-info-slice';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { unloadRelationshipState } from '../../../account-info/features/relationships/relationships-slice';
import { unloadDependentState } from '../../../account-info/features/dependents/dependents-slice';
import { OfficerAdminItemView } from './institution-officer-item-view';
import { OfficerAdminState, Officer } from './institution-officers-models';
import { setCurrentEmployee, unSetEmploymentState } from '../employment/employment-slice';
import { OfficersListLabels } from './institution-offices-labels';
import { InstitutionState } from '../institutions/institution-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { getOfficersList, reInviteOfficerOrAdmin, removeOfficer } from '../../../../global/ducks/wam-api';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { dispatchSetOfficers, setReinviteStatus, setRemoveStatus, stageOfficerAdminToDelete } from './institution-officers-slices';
import { ApiStatuses } from '../../../../global/models/api-model';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { TcsmModal } from '../../../../global/components/tcsm-modal';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { setEmployeeContext, setNavigationContext } from '../../../../global/ducks/app-context';
import { EmployeeContext, NavigationContext } from '../../../../global/models/app-context-model';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { AuthenticatedState } from '../../../../global/models/authenticated-model';
import { BaseAppState } from '../../../../global/models/redux-state';
import { useRoleObject } from '../../components/useRoles';
import { SeniorOfficer } from '../../constants/constant-strings';
import { OfficerRemoveConfirmation } from './officer-remove-modal-content';

function OfficerSearch() {

	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);
	const officerState = useSelector<TcsmStates, OfficerAdminState>(state => state.officerAdminState);
	const dispatch = useDispatch();
	const tableClasses = sortTableStyles();
	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);
	const authenticatedState = useSelector<BaseAppState, AuthenticatedState>(state => state.authenticatedState);

	const { state } = useLocation<any>();
	const routeMatch = useRouteMatch();
	const history = useHistory();
	const demographic = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.PersonalInformation}`);
	const initialList = officerState?.officers;
	const roles = useRoleObject();
	let seniorLevel = false;
	const showRemove = true;
	const classes = buttonStyles();

	const clientId = authenticatedState?.clientGuid;

	const [open, setOpen] = React.useState(false);
	const [officerRemoveOpen, setOfficerRemoveOpen] = React.useState(false);

	const handleClose = () => {
		setOpen(false);
	};

	const handleOfficerRemoveClose = () => {
		setOfficerRemoveOpen(false);
	};



	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(unSetEmploymentState());
		dispatch(unloadCompensationState());
		dispatch(unloadPersonState(DemographicAppKeys.arc));
		dispatch(unloadRelationshipState(DemographicAppKeys.arc));
		dispatch(unloadDependentState(DemographicAppKeys.arc));
	}, []);

	useEffect(() => {
		if (institutionState?.currentInstitution?.org_guid) {
			dispatch(
				getOfficersList(institutionState?.currentInstitution?.org_guid,
					(response) => {
						dispatchSetOfficers(response, dispatch);
					}));
		}
	}, [institutionState?.currentInstitution?.org_guid]);

	const onSetOfficer = (guid: string) => {
		const match = initialList?.filter(x => x.guid === guid)[0];
		if (match) {
			dispatch(setCurrentEmployee(match));
			dispatch(setEmployeeContext(EmployeeContext.InstitutionAdmin));
			dispatch(setNavigationContext(NavigationContext.Institution));
		}

		history.push(demographic);
	};

	const onReinviteSuccess = () => {
		setOpen(true);
		dispatch(setReinviteStatus(ApiStatuses.Success));
	};


	const onReinviteFail = () => {
		setOpen(true);
		dispatch(setReinviteStatus(ApiStatuses.Failure));
	};

	const reInvite = (guid: string) => {
		dispatch(reInviteOfficerOrAdmin(guid, onReinviteSuccess, onReinviteFail));
	};

	const onRemoveSuccess = () => {
		setOfficerRemoveOpen(false);
		dispatch(setRemoveStatus(ApiStatuses.Success));
		dispatch(
			getOfficersList(institutionState?.currentInstitution?.org_guid,
				(response) => {
					dispatchSetOfficers(response, dispatch);
				}));
	};


	const onRemovFail = () => {
		setOfficerRemoveOpen(false);
		dispatch(setRemoveStatus(ApiStatuses.Failure));
	};

	const cancelRemove = () => {
		dispatch(stageOfficerAdminToDelete(null));
		setOfficerRemoveOpen(false);
	};

	const remove = (officer: Officer) => {
		dispatch(stageOfficerAdminToDelete(officer));
		setOfficerRemoveOpen(true);
	};

	const confirmOfficerRemove = (rolesToRemove: string[]) => {
		dispatch(
			removeOfficer(
				officerState?.officerToRemove,
				rolesToRemove,
				onRemoveSuccess,
				onRemovFail),
		);
	};


	if (roles?.employee_roster?.filter(a => a?.role_name === SeniorOfficer && a?.role_target === institutionState.institutionClientNumber)?.length > 0) seniorLevel = true;

	if (!institutionState?.institutionClientNumber) {
		return (
			<Redirect to=
				{
					{
						pathname: institutionSelect,
						state: {
							actionLink: routeMatch.path,
							finalAction: state?.actionLink,
						},
					}
				}
			/>);
	}

	if (!officerState.officersLoaded) {
		return (<Loader />);
	}



	return (
		<>
			<TcsmModal
				open={open}
				handleClose={handleClose}

			>
				<>
					<div className='rich-text'>
						<h2>{searchSettings.inviteOfficerTitle}</h2>
						<p>{officerState.reInviteStatus === ApiStatuses.Success ? searchSettings.inviteOfficerSuccessModalDescription : searchSettings.inviteOfficerFailModalDescription}</p>
					</div>
					<button type='button' onClick={handleClose}>{searchSettings.inviteOfficerConfirmButton}</button>
				</>
			</TcsmModal>
			<TcsmModal
				open={officerRemoveOpen}
				handleClose={handleOfficerRemoveClose}
			>
				<OfficerRemoveConfirmation
					officerToRemove={officerState.officerToRemove}
					settings={searchSettings}
					cancelAction={cancelRemove}
					confirmAction={confirmOfficerRemove}
				/>

			</TcsmModal>
			<ContentSpacer>
				<Grid container>
					<MainContent>
						<PageHeader
							headerText={searchSettings?.officerListTitle}
							description={searchSettings?.officerListDescription}
						/>

						<Link className={classes.secondaryButton} to={`${routeMatch.path}${ArcRoutes.AddInstitutionAdministrator}`}>{searchSettings.addOfficerButtonText}</Link>

					</MainContent>
					<Hidden smDown>
						<SideBarContent>
							<GuidedProcessSupportBlock title={gpsSettings?.groupSearchGpsTitle} contents={gpsSettings?.groupSearchGps} />
						</SideBarContent>
					</Hidden>
				</Grid>
			</ContentSpacer>
			<SearchApp initialList={initialList} appName={`${institutionState?.institutionClientNumber}-OfficerSearch`}>
				<div className={tableClasses.sortTable}>
					<Hidden xsDown>
						<OfficersListLabels settings={searchSettings} isSenior={seniorLevel} />
					</Hidden>
					<div className='sort-table-content'>
						<SearchList
							renderList={
								(item) => {
									const itemShowRemove = showRemove && (clientId !== item.guid);
									return <OfficerAdminItemView key={item.guid} item={item} settings={searchSettings} onSetOfficer={onSetOfficer} reIniviteOfficer={reInvite} removeOfficer={remove} showAction={seniorLevel} showRemove={itemShowRemove} ></OfficerAdminItemView>;
								}
							}
							renderPagination={
								(totalCounts, name) => {
									return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 10)} nextButton={searchSettings.nextButton} prevButton={searchSettings.previousButton} />;
								}
							}
							emptyMessage={searchSettings.emptyListMessage}
						/>
					</div>
				</div>
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.groupSearchGpsTitle} contents={gpsSettings?.groupSearchGps} />
					</SideBarContent>
				</Hidden>
			</SearchApp>
		</>
	);
}

export default OfficerSearch;